import { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import { useLocalParticipant } from '@livekit/components-react';
import { useRoom } from '../room/RoomProvider';
import type { HighResFrameConfig } from '@modelcast/types';
import { DEFAULT_HIGHRES_CONFIG } from '@modelcast/types';
import { enqueueReferencePackAsset } from './ReferencePackAssetUploadQueue';
import { getCameraDeviceKey, getStoredCameraDeviceLabel } from './cameraDeviceLabels';

interface UseModelFrameStreamOptions {
  videoRef: React.RefObject<HTMLVideoElement>;
  config?: Partial<HighResFrameConfig>;
  enabled?: boolean;
  frameIntervalMs?: number;
  captureKey?: string | number | null;
  queueCaptures?: boolean;
  captureContext?: {
    modelProfileId: string | null;
    poseName: string | null;
    poseIndex: number | null;
    cameraId: string | null;
    cameraLabel: string | null;
  };
}

interface UseModelFrameStreamResult {
  isStreaming: boolean;
  isCapturing: boolean;
  lastFrameTime: number | null;
  fps: number;
  startStreaming: () => void;
  stopStreaming: () => void;
}

function getVideoCaptureCameraInfo(video: HTMLVideoElement, roomCode: string) {
  const stream = video.srcObject instanceof MediaStream ? video.srcObject : null;
  const track = stream?.getVideoTracks()[0] ?? null;
  if (!track) return null;

  const cameraId = getCameraDeviceKey(track.getSettings(), track.id);
  return {
    cameraId,
    cameraLabel: getStoredCameraDeviceLabel(roomCode, cameraId, 0),
  };
}

export function useModelFrameStream(
  options: UseModelFrameStreamOptions
): UseModelFrameStreamResult {
  const {
    videoRef,
    config: userConfig,
    enabled = true,
    frameIntervalMs = 10000,
    captureKey = null,
    queueCaptures = false,
    captureContext,
  } = options;
  const { modelParticipantId, roomCode } = useRoom();
  const { localParticipant } = useLocalParticipant();
  const [isStreaming, setIsStreaming] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [lastFrameTime, setLastFrameTime] = useState<number | null>(null);
  const [fps, setFps] = useState(0);
  const streamingRef = useRef(false);
  const capturingRef = useRef(false);
  const frameIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const frameCountRef = useRef(0);
  const lastCaptureAtRef = useRef<number | null>(null);
  const fpsIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const config: HighResFrameConfig = useMemo(() => ({
    ...DEFAULT_HIGHRES_CONFIG,
    ...userConfig,
  }), [
    userConfig?.chunkSize,
    userConfig?.format,
    userConfig?.progressive,
    userConfig?.quality,
    userConfig?.resolution,
  ]);

  // Get resolution dimensions
  const getResolutionDimensions = (
    resolution: '720p' | '1080p' | '4k' | '8k' | 'native',
    video: HTMLVideoElement
  ): { width: number; height: number } => {
    switch (resolution) {
      case '720p':
        return { width: 1280, height: 720 };
      case '1080p':
        return { width: 1920, height: 1080 };
      case '8k':
        return { width: 7680, height: 4320 };
      case '4k':
        return { width: 3840, height: 2160 };
      case 'native':
      default:
        // Use actual video dimensions (what the camera is providing)
        return { width: video.videoWidth, height: video.videoHeight };
    }
  };

  const captureFrame = useCallback(async () => {
    if (!videoRef.current || !enabled) return false;

    const video = videoRef.current;
    if (video.readyState < 2) {
      console.warn('[Sender] Video not ready, readyState:', video.readyState);
      return false;
    }

    setIsCapturing(true);
    const captureStartTime = Date.now();

    try {
      const targetDims = getResolutionDimensions(config.resolution, video);

      // Skip if video has no dimensions
      if (video.videoWidth === 0 || video.videoHeight === 0) {
        console.warn('[Sender] Video has no dimensions, skipping');
        setIsCapturing(false);
        return false;
      }

      // Create or reuse canvas
      if (
        !canvasRef.current ||
        canvasRef.current.width !== targetDims.width ||
        canvasRef.current.height !== targetDims.height
      ) {
        canvasRef.current = document.createElement('canvas');
        canvasRef.current.width = targetDims.width;
        canvasRef.current.height = targetDims.height;
      }

      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d', { alpha: false });
      if (!ctx) throw new Error('Failed to get canvas context');

      // Draw video frame to canvas at target resolution
      ctx.drawImage(video, 0, 0, targetDims.width, targetDims.height);

      // Encode to blob
      const encodeStartTime = Date.now();
      const mimeType =
        config.format === 'avif' ? 'image/avif' : `image/${config.format}`;
      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (b) => {
            if (b) resolve(b);
            else reject(new Error('Failed to create blob'));
          },
          mimeType,
          config.quality / 100
        );
      });

      const encodeTime = Date.now() - encodeStartTime;
      const capturedAt = Date.now();

      const captureParticipantId = localParticipant?.identity ?? modelParticipantId;
      if (queueCaptures && captureParticipantId) {
        const fallbackCameraInfo = captureContext?.cameraId && captureContext.cameraLabel
          ? null
          : getVideoCaptureCameraInfo(video, roomCode);
        await enqueueReferencePackAsset({
          roomCode,
          modelParticipantId: captureParticipantId,
          modelProfileId: captureContext?.modelProfileId ?? null,
          blob,
          capturedAt,
          width: targetDims.width,
          height: targetDims.height,
          format: config.format,
          poseName: captureContext?.poseName ?? null,
          poseIndex: captureContext?.poseIndex ?? null,
          cameraId: captureContext?.cameraId ?? fallbackCameraInfo?.cameraId ?? null,
          cameraLabel: captureContext?.cameraLabel ?? fallbackCameraInfo?.cameraLabel ?? null,
        });
      }

      const totalTime = Date.now() - captureStartTime;
      const sizeKB = (blob.size / 1024).toFixed(1);

      console.log(`[Sender] Still captured: ${sizeKB}KB, encode=${encodeTime}ms, total=${totalTime}ms`);

      setLastFrameTime(Date.now());
      return true;
    } catch (error) {
      console.error('[Sender] Error:', error);
      return false;
    } finally {
      setIsCapturing(false);
    }
  }, [videoRef, enabled, config, queueCaptures, localParticipant?.identity, modelParticipantId, roomCode, captureContext]);

  useEffect(() => {
    lastCaptureAtRef.current = null;
  }, [captureKey]);

  // Capture a single still, ensuring no overlap with the previous capture
  const captureIfReady = useCallback(async () => {
    if (!streamingRef.current) return;

    // Skip if still capturing the previous still
    if (capturingRef.current) {
      console.log('[useModelFrameStream] Skipping still - previous capture is still running');
      return;
    }

    const now = Date.now();
    if (lastCaptureAtRef.current && now - lastCaptureAtRef.current < frameIntervalMs) {
      console.log('[useModelFrameStream] Skipping frame - capture interval has not elapsed', {
        elapsedMs: now - lastCaptureAtRef.current,
        frameIntervalMs,
        captureKey,
      });
      return;
    }

    capturingRef.current = true;
    try {
      const captured = await captureFrame();
      if (captured) {
        lastCaptureAtRef.current = Date.now();
        frameCountRef.current++;
      }
    } finally {
      capturingRef.current = false;
    }
  }, [captureFrame, captureKey, frameIntervalMs]);

  const startStreaming = useCallback(() => {
    if (streamingRef.current) return;

    console.log('[useModelFrameStream] Starting streaming at interval:', frameIntervalMs, 'ms');
    streamingRef.current = true;
    capturingRef.current = false;
    setIsStreaming(true);
    frameCountRef.current = 0;

    // Capture first frame immediately
    captureIfReady();

    // Track FPS
    fpsIntervalRef.current = setInterval(() => {
      setFps(frameCountRef.current);
      frameCountRef.current = 0;
    }, 1000);
  }, [captureIfReady, frameIntervalMs]);

  useEffect(() => {
    if (!isStreaming) return;

    if (frameIntervalRef.current) {
      clearInterval(frameIntervalRef.current);
    }

    const pollIntervalMs = Math.min(1000, frameIntervalMs);

    console.log('[useModelFrameStream] Capture interval active:', {
      frameIntervalMs,
      pollIntervalMs,
      captureKey,
    });

    frameIntervalRef.current = setInterval(() => {
      captureIfReady();
    }, pollIntervalMs);

    return () => {
      if (frameIntervalRef.current) {
        clearInterval(frameIntervalRef.current);
        frameIntervalRef.current = null;
      }
    };
  }, [captureIfReady, captureKey, frameIntervalMs, isStreaming]);

  const stopStreaming = useCallback(() => {
    streamingRef.current = false;
    capturingRef.current = false;
    setIsStreaming(false);

    if (frameIntervalRef.current) {
      clearInterval(frameIntervalRef.current);
      frameIntervalRef.current = null;
    }

    if (fpsIntervalRef.current) {
      clearInterval(fpsIntervalRef.current);
      fpsIntervalRef.current = null;
    }

    setFps(0);
  }, []);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      stopStreaming();
    };
  }, [stopStreaming]);

  return {
    isStreaming,
    isCapturing,
    lastFrameTime,
    fps,
    startStreaming,
    stopStreaming,
  };
}
